import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import { useAuth } from "@/lib/auth";
import { riskBand } from "@/lib/format";
import { X, Tag as TagIcon, Users, User, Plus, PenLine, ClipboardList, Wrench, MapPin, Building2, Layers } from "lucide-react";

const PRIORITIES = ["low", "normal", "high", "critical"];
const STATUSES = ["open", "in_progress", "blocked", "completed"];

/** 8 Wastes with 1-sentence definitions + a few real-floor examples the user requested. */
const WASTES = [
  { key: "transport", label: "Transport",
    def: "Moving materials, product or paperwork farther than needed to get the job done.",
    examples: "Forklift trips across the plant · re-routing paperwork between offices · shuttling WIP between disconnected cells." },
  { key: "inventory", label: "Inventory",
    def: "Excess raw material, WIP or finished goods that sit around costing money and hiding problems.",
    examples: "Overstocked stockroom · pallets of WIP piled between stations · finished goods sitting weeks before ship." },
  { key: "motion", label: "Motion",
    def: "Unnecessary movement of people or machines that adds no value to the customer.",
    examples: "Walking across the line to grab a tool · reaching above shoulder for parts · double-handling of components." },
  { key: "waiting", label: "Waiting",
    def: "Idle time for parts, people or information — anything not flowing to the next step.",
    examples: "Line stopped waiting for QA sign-off · operator waiting for a supervisor · machine paused for changeover." },
  { key: "overproduction", label: "Overproduction",
    def: "Making more, sooner or faster than the next step needs — the worst waste because it hides others.",
    examples: "Building ahead of the pull signal · batch-running a whole shift when only 50 units are needed · pre-printing labels that get scrapped." },
  { key: "overprocessing", label: "Overprocessing",
    def: "Doing more work, adding features or using tighter tolerances than the customer actually pays for.",
    examples: "Extra polishing / painting the customer never sees · triple-checking a signed doc · gold-plating a low-spec part." },
  { key: "defects", label: "Defects",
    def: "Anything that has to be re-worked, scrapped or corrected because it wasn't right the first time.",
    examples: "Mis-labelled cartons · off-spec fill weights · rework loops on a weld cell · customer complaints from wrong shipments." },
  { key: "skills", label: "Skills (underutilised)",
    def: "Not tapping into people's ideas, training or capabilities — the wasted human talent on the floor.",
    examples: "Not asking the operator running the machine for improvement ideas · under-training a floor lead · ignoring near-miss reports." },
];

const LIKELIHOOD_DESC = {
  1: "Rare — once a decade or less",
  2: "Unlikely — once every couple years",
  3: "Possible — once a year",
  4: "Likely — once a quarter or more",
  5: "Almost certain — monthly or weekly",
};
const SEVERITY_DESC = {
  1: "Negligible — cosmetic / minor delay",
  2: "Minor — small rework, brief downtime",
  3: "Moderate — extended downtime, productivity hit",
  4: "Major — significant breakage, injury risk",
  5: "Severe — catastrophic breakage, serious injury or death",
};

/** PDCA stages, rendered vertically per user request. Only Plan is required at creation time.
 *  Do / Check / Act are informational — captured post-creation on Action Detail page. */
const PDCA_STAGES = [
  {
    k: "P", label: "PLAN",
    tagline: "Define the problem, root cause, target and how you'll know it worked.",
    color: "#1a1a1a",
    accent: "#ff4500",
  },
  {
    k: "D", label: "DO",
    tagline: "Execute the countermeasure. Log evidence — photos, files, chat — on the action page.",
    color: "#1a1a1a", accent: "#d6a900",
    hint: "Available after the action is created — assignees add notes/photos on the action detail page.",
  },
  {
    k: "C", label: "CHECK",
    tagline: "Verify. Did the target hold? Did the metric move? 30-day effectiveness review kicks in automatically.",
    color: "#1a1a1a", accent: "#1a8754",
    hint: "The system triggers a 30-day effectiveness review when the action is closed. Verify with proof to auto-archive.",
  },
  {
    k: "A", label: "ACT",
    tagline: "Standardise the win, or re-open and cycle again. Compliance trace is preserved either way.",
    color: "#1a1a1a", accent: "#ff5f15",
    hint: "If Check fails, the action is re-opened. If it holds, the standard is locked in and archived.",
  },
];

export default function AddActionDialog({ open, onClose, onCreated, initial = null }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "", description: "", tags: [],
    assigned_user_ids: [], assigned_team_ids: [],
    due_date: "", priority: "normal", status: "open",
    photo_ids: [], file_ids: [], waste_category: "",
    likelihood: 1, severity: 1,
    equipment: "", location: "", department: "", type_tag: "",
    signature_name: "",
  });
  const [newTag, setNewTag] = useState("");
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [orgTags, setOrgTags] = useState({ equipment: [], location: [], department: [], type: [] });

  useEffect(() => {
    if (!open) return;
    api.get("/teams").then(async (r) => {
      setTeams(r.data);
      const memberLists = await Promise.all(r.data.map((t) => api.get(`/teams/${t.id}/members`).then((m) => m.data).catch(() => [])));
      const seen = new Set();
      const flat = [];
      memberLists.flat().forEach(({ user: u }) => {
        if (u && !seen.has(u.id)) { seen.add(u.id); flat.push(u); }
      });
      setAllUsers(flat);
    }).catch(() => {});
    api.get("/tags").then((r) => setOrgTags(r.data || { equipment: [], location: [], department: [], type: [] })).catch(() => {});
    if (initial) {
      setForm({
        title: initial.title || "", description: initial.description || "",
        tags: initial.tags || [],
        assigned_user_ids: initial.assigned_user_ids || [],
        assigned_team_ids: initial.assigned_team_ids || [],
        due_date: initial.due_date || "",
        priority: initial.priority || "normal",
        status: initial.status || "open",
        photo_ids: initial.photo_ids || [],
        file_ids: initial.file_ids || [],
        waste_category: initial.waste_category || "",
        likelihood: initial.likelihood || 1,
        severity: initial.severity || 1,
        equipment: initial.equipment || "",
        location: initial.location || "",
        department: initial.department || "",
        type_tag: initial.type_tag || "",
        signature_name: user?.name || "",
      });
    } else {
      setForm({
        title: "", description: "", tags: [],
        assigned_user_ids: [], assigned_team_ids: [],
        due_date: "", priority: "normal", status: "open",
        photo_ids: [], file_ids: [], waste_category: "",
        likelihood: 1, severity: 1,
        equipment: "", location: "", department: "", type_tag: "",
        signature_name: user?.name || "",
      });
    }
  }, [open, initial, user]);

  const toggle = (key, id) => {
    const arr = form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id];
    setForm({ ...form, [key]: arr });
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const payload_signature = user?.name || form.signature_name || "";
    setBusy(true);
    try {
      const payload = {
        ...form,
        signature_name: payload_signature,
        due_date: form.due_date || null,
        likelihood: Number(form.likelihood),
        severity: Number(form.severity),
        equipment: form.equipment || null,
        location: form.location || null,
        department: form.department || null,
        type_tag: form.type_tag || null,
      };
      const r = initial?.id
        ? await api.patch(`/actions/${initial.id}`, payload)
        : await api.post("/actions", payload);
      toast.success(initial?.id ? "Action updated" : "Action created");
      onCreated?.(r.data);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const riskScore = Number(form.likelihood) * Number(form.severity);
  const band = riskBand(riskScore);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-[calc(100vw-2rem)] rounded-2xl border-[#1a1a1a]/15 max-h-[92vh] overflow-y-auto overflow-x-hidden scrollbar-thin p-0">
        <div className="sticky top-0 bg-white border-b border-[#1a1a1a]/10 px-6 py-4 z-10">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-xl flex items-center gap-2">
              <span className="w-8 h-8 rounded-xl bg-[#ff4500] text-white flex items-center justify-center"><Plus className="w-4 h-4" /></span>
              {initial?.id ? `Edit action ${initial.display_id ? "· " + initial.display_id : ""}` : "New action"}
              <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/50">// PDCA cycle</span>
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* PLAN — filled at creation */}
          <PdcaSection stage={PDCA_STAGES[0]}>
            <div className="space-y-4">
              <Field label={<span>Title * <span className="text-[10px] font-mono text-[#1a1a1a]/45 normal-case">(60 chars max — keep it short: &ldquo;Repair belt on Machine 7&rdquo;)</span></span>}>
                <input data-testid="action-title-input" value={form.title} maxLength={60} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Short, action-oriented title…"
                  className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a] focus:ring-2 focus:ring-[#1a1a1a]/15" />
                <div className="text-right text-[10px] font-mono text-[#1a1a1a]/40 mt-0.5">{form.title.length}/60</div>
              </Field>
              <Field label="Description / problem statement">
                <textarea data-testid="action-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What is broken, missing or wasteful? Be specific — this feeds the 5-Whys later."
                  className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm h-20 focus:outline-none focus:border-[#1a1a1a]" />
              </Field>

              {/* Structured tag dropdowns — Equipment / Location / Department / Type */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <TagField icon={Wrench} label="Equipment" value={form.equipment} onChange={(v) => setForm({ ...form, equipment: v })}
                  options={orgTags.equipment || []} testId="action-equipment-input" />
                <TagField icon={MapPin} label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })}
                  options={orgTags.location || []} testId="action-location-input" />
                <TagField icon={Building2} label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })}
                  options={orgTags.department || []} testId="action-department-input" />
                <TagField icon={Layers} label="Type" value={form.type_tag} onChange={(v) => setForm({ ...form, type_tag: v })}
                  options={orgTags.type || []} testId="action-type-input" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Due date">
                  <input data-testid="action-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
                </Field>
                <Field label="Priority">
                  <select data-testid="action-priority-input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select data-testid="action-status-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                </Field>
              </div>

              {/* 8 Wastes — expandable multi-select with definitions + examples inline (iter29) */}
              <WasteMultiSelect
                selected={(form.waste_category || "").split(",").map((s) => s.trim()).filter(Boolean)}
                onChange={(arr) => setForm({ ...form, waste_category: arr.join(",") })}
              />

              {/* Risk matrix — smaller + read-only (iter29 — computed from L × S, no clickable table) */}
              <div className="bg-[#f7f5f2] border border-[#1a1a1a]/08 rounded-xl p-4 space-y-3" data-testid="action-risk-matrix">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Risk Register — Likelihood × Severity</div>
                    <div className="font-display font-bold text-sm text-[#1a1a1a]/75">Pick L and S below — score updates automatically.</div>
                  </div>
                  {band && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">Score</div>
                      <div className="font-display font-black text-3xl leading-none" style={{ color: band.color }}>{riskScore}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: band.color }}>{band.label}</div>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Likelihood (1–5)">
                    <select data-testid="action-likelihood-input" value={form.likelihood}
                      onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })}
                      className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                      {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} — {LIKELIHOOD_DESC[n]}</option>)}
                    </select>
                  </Field>
                  <Field label="Severity (1–5)">
                    <select data-testid="action-severity-input" value={form.severity}
                      onChange={(e) => setForm({ ...form, severity: Number(e.target.value) })}
                      className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm">
                      {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n} — {SEVERITY_DESC[n]}</option>)}
                    </select>
                  </Field>
                </div>
                <RiskMatrix L={Number(form.likelihood)} S={Number(form.severity)} />
                {riskScore >= 12 && (
                  <div className="text-[11px] font-mono uppercase tracking-wider font-bold text-[#ff4500] bg-[#fff1ea] border border-[#ff4500]/30 rounded-md px-3 py-2">
                    ⚠ Score {riskScore} ({band?.label}) — 5-Whys root cause must be completed before this action can be closed.
                  </div>
                )}
              </div>

              {/* Free-text tags (in addition to structured ones) */}
              <Field label={<span className="inline-flex items-center gap-1.5"><TagIcon className="w-3 h-3" /> Additional tags</span>}>
                <div className="flex gap-2">
                  <input data-testid="action-new-tag" value={newTag} onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newTag) { setForm({ ...form, tags: [...form.tags, newTag] }); setNewTag(""); } } }}
                    placeholder="press enter to add" className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm" />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {form.tags.map((t, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-[#fff1ea] text-[#1a1a1a] text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full">
                      {t}
                      <button onClick={() => setForm({ ...form, tags: form.tags.filter((_, idx) => idx !== i) })}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label={<span className="inline-flex items-center gap-1.5"><User className="w-3 h-3" /> Assign to people</span>}>
                  <div className="flex flex-wrap gap-1.5">
                    {allUsers.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">Join a hub to assign teammates.</span>}
                    {allUsers.map((u) => {
                      const on = form.assigned_user_ids.includes(u.id);
                      return (
                        <button key={u.id} data-testid={`assign-user-${u.id}`} type="button" onClick={() => toggle("assigned_user_ids", u.id)}
                          className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                          {u.name}
                        </button>
                      );
                    })}
                  </div>
                </Field>
                <Field label={<span className="inline-flex items-center gap-1.5"><Users className="w-3 h-3" /> Assign to hubs</span>}>
                  <div className="flex flex-wrap gap-1.5">
                    {teams.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No hubs yet.</span>}
                    {teams.map((t) => {
                      const on = form.assigned_team_ids.includes(t.id);
                      return (
                        <button key={t.id} data-testid={`assign-team-${t.id}`} type="button" onClick={() => toggle("assigned_team_ids", t.id)}
                          className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#ff4500] border-[#ff4500]/30"}`}>
                          {t.name}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </div>

              <Field label="Attach photos">
                <PhotoUploader photoIds={form.photo_ids} onChange={(ids) => setForm({ ...form, photo_ids: ids })} testIdPrefix="action" />
              </Field>
            </div>
          </PdcaSection>

          {/* DO / CHECK / ACT — informational panes */}
          {PDCA_STAGES.slice(1).map((s) => (
            <PdcaSection key={s.k} stage={s} collapsed hint={s.hint}>
              <div className="text-[12px] text-[#1a1a1a]/70 leading-relaxed" data-testid={`pdca-hint-${s.k}`}>
                <ClipboardList className="w-3.5 h-3.5 inline mr-1 text-[#1a1a1a]/55" />
                {s.hint}
              </div>
            </PdcaSection>
          ))}

          {/* Signature — locked to profile */}
          <div className="bg-[#1a1a1a] text-white rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <PenLine className="w-4 h-4 text-[#ff5f15]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5f15]">// Signed automatically from your profile</div>
                <div className="font-display font-bold text-sm" data-testid="action-signature-display">{user?.name || "—"}{user?.employee_id ? ` · ${user.employee_id}` : ""}</div>
              </div>
            </div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/55">
              {new Date().toISOString().slice(0, 19).replace("T", " ")} · edit signature in <span className="text-[#ff5f15]">Profile → Edit</span>
            </div>
          </div>
        </div>

        <DialogFooter className="sticky bottom-0 bg-white border-t border-[#1a1a1a]/10 px-6 py-3">
          <button data-testid="action-submit-button" onClick={submit} disabled={busy} className="btn-pill btn-pill-primary">
            {busy ? "Saving…" : initial?.id ? "Save changes" : "Create action"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PdcaSection({ stage, children, collapsed, hint }) {
  const [open, setOpen] = useState(!collapsed);
  return (
    <section className="bg-white border border-[#1a1a1a]/10 rounded-2xl overflow-hidden" data-testid={`pdca-section-${stage.k}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#faf8f4] text-left">
        <span className="w-9 h-9 rounded-lg text-white font-display font-black text-lg flex items-center justify-center" style={{ background: stage.accent }}>
          {stage.k}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: stage.accent }}>// {stage.label}</div>
          <div className="font-display font-bold text-sm text-[#1a1a1a] truncate">{stage.tagline}</div>
          {!open && hint && (
            <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5 truncate" data-testid={`pdca-hint-collapsed-${stage.k}`}>{hint}</div>
          )}
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">{open ? "collapse" : "expand"}</span>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-[#1a1a1a]/06">{children}</div>}
    </section>
  );
}

function TagField({ icon: Icon, label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono inline-flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </label>
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-2 py-2 text-sm">
        <option value="">— select —</option>
        {options.map((o) => <option key={o.id || o.label} value={o.label}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1.5 font-mono">{label}</label>
      {children}
    </div>
  );
}

function RiskMatrix({ L, S }) {
  // Iter29 — read-only, smaller (150px max). Score comes from the L × S selects above.
  const cell = (l, s) => {
    const score = l * s;
    const b = riskBand(score);
    const active = l === L && s === S;
    return (
      <div key={`${l}-${s}`} data-testid={`risk-cell-${l}-${s}`}
        className={`relative aspect-square rounded text-[10px] font-display font-black flex items-center justify-center transition-all duration-150 ${active ? "ring-2 ring-[#1a1a1a] shadow-md" : ""}`}
        style={{ background: b.color, color: "white" }}>
        {score}
      </div>
    );
  };
  return (
    <div className="bg-white rounded-xl p-2 border border-[#1a1a1a]/08 max-w-[220px] mx-auto" aria-hidden>
      <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mb-1 text-center">Likelihood →</div>
      <div className="flex gap-1">
        <div className="flex flex-col-reverse justify-around">
          <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 [writing-mode:vertical-rl] rotate-180 self-center">Severity</div>
        </div>
        <div className="flex-1 grid grid-cols-5 gap-0.5">
          {[5,4,3,2,1].map((s) => (
            [1,2,3,4,5].map((l) => cell(l, s))
          ))}
        </div>
      </div>
    </div>
  );
}

// Iter29 — 8 Wastes multi-select with definitions + examples visible inline.
function WasteMultiSelect({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (key) => {
    if (selected.includes(key)) onChange(selected.filter((k) => k !== key));
    else onChange([...selected, key]);
  };
  const selectedLabels = selected.map((k) => WASTES.find((w) => w.key === k)?.label).filter(Boolean);
  return (
    <div className="space-y-2" data-testid="waste-multiselect">
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#1a1a1a]/60 font-bold">
        8 Wastes classification — check any that apply (definitions + examples inline)
      </div>
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid="waste-toggle"
        className="w-full bg-white border border-[#1a1a1a]/15 rounded-xl px-3 py-2 text-sm text-left hover:border-[#ff4500] transition-colors flex items-center justify-between gap-2">
        <span className="truncate">
          {selectedLabels.length === 0 ? <span className="text-[#1a1a1a]/45">Click to see definitions and pick one or more…</span> :
            <span className="text-[#1a1a1a]"><strong>{selectedLabels.length} selected:</strong> {selectedLabels.join(", ")}</span>}
        </span>
        <span className="text-[10px] font-mono text-[#ff4500] font-bold flex-shrink-0">{open ? "close" : "open"}</span>
      </button>
      {open && (
        <ul className="bg-white border border-[#1a1a1a]/15 rounded-xl divide-y divide-[#1a1a1a]/06 max-h-[420px] overflow-auto scrollbar-thin">
          {WASTES.map((w) => {
            const on = selected.includes(w.key);
            return (
              <li key={w.key} className={`p-3 cursor-pointer transition-colors ${on ? "bg-[#fff1ea]" : "hover:bg-[#faf8f4]"}`} onClick={() => toggle(w.key)}
                data-testid={`waste-option-${w.key}`}>
                <div className="flex items-start gap-2">
                  <input type="checkbox" checked={on} readOnly className="mt-1 accent-[#ff4500]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[13px] text-[#1a1a1a]">{w.label}</div>
                    <div className="text-[11px] text-[#1a1a1a]/75 mt-0.5">{w.def}</div>
                    <div className="text-[10px] text-[#1a1a1a]/55 mt-1 italic"><strong>Examples:</strong> {w.examples}</div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
